import { z } from 'zod';

/** One expense type from `GET /api/v1/expense-type`. Only consumed fields typed (rest ignored). */
const ExpenseTypeDtoSchema = z.object({
  _id: z.string(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  // Base64 data-URI category icon (or URL).
  image: z.string().nullish(),
  color: z.string().nullish(),
});

/** Envelope: `{ status, message, data: { expense_types } }`. `data` is nullish-tolerant (→ empty). */
export const ExpenseTypesResponseSchema = z.object({
  status: z.boolean().optional(),
  message: z.string().optional(),
  data: z.object({ expense_types: z.array(ExpenseTypeDtoSchema).default([]) }).nullish(),
});

export type ExpenseTypeDto = z.infer<typeof ExpenseTypeDtoSchema>;

/**
 * Envelope of `POST /api/v1/expense-type/classify-expense` (body `{ description }`): the classified
 * category lives at `data.expense_type { _id, name }` — but the field has also been seen as a bare
 * id string and as flat `data.{_id|id|expense_type_id, name|expense_type_name}`, so every shape is
 * tolerated and the mapper picks whichever id/name is present. A `message` of "Cancelled" (a
 * superseded/aborted classify) is ignored by the command.
 */
export const ClassifyExpenseResponseSchema = z.object({
  status: z.boolean().optional(),
  message: z.string().nullish(),
  data: z
    .object({
      expense_type: z
        .union([
          z.string(),
          z.object({
            _id: z.string().nullish(),
            id: z.string().nullish(),
            name: z.string().nullish(),
          }),
        ])
        .nullish(),
      expense_type_id: z.string().nullish(),
      expense_type_name: z.string().nullish(),
      _id: z.string().nullish(),
      id: z.string().nullish(),
      name: z.string().nullish(),
    })
    .nullish(),
});

export type ClassifyExpenseResponse = z.infer<typeof ClassifyExpenseResponseSchema>;
